import { Loader2 } from "lucide-react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import BusinessSearchForm from "../components/business-search-form";
import BusinessCard from "../components/business-card";
import Pagination from "../components/pagination";
import type { BusinessSearchValues } from "../components/business-search-form";
import { useBusinesses } from "../hooks/useBusinesses";
import { useWellKnown } from "../hooks/useWellKnown";
import "../styles/pages/home.css";

const PAGE_SIZE = 12;

export default function HomePage() {
  const { data: appConfig } = useWellKnown();
  const urlSearch = useSearch({ from: "/" });
  const navigate = useNavigate();

  const searchParams: BusinessSearchValues = {
    search: urlSearch.search ?? "",
    address: urlSearch.address ?? "",
    distance: urlSearch.distance ?? "99999",
  };
  const page = urlSearch.page ?? 1;

  const { data, isLoading, isError, isFetching } = useBusinesses(searchParams);

  const allBusinesses = data?.data ?? [];
  const total = allBusinesses.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const businesses = allBusinesses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSearch(values: BusinessSearchValues) {
    navigate({ to: "/", search: values });
  }

  function handlePageChange(newPage: number) {
    navigate({
      to: "/",
      search: (prev) => ({ ...prev, page: newPage > 1 ? newPage : undefined }),
    });
  }

  return (
    <div className="home-page">
      <section className="hero" style={{ backgroundImage: "url('/banner.jpg')" }}>
        <div className="hero__content hero__content--wide">
          {appConfig?.logo_url && (
            <img
              src={appConfig.logo_url}
              alt={appConfig.business_name ?? "Logo"}
              className="hero__logo"
            />
          )}
          <h1 className="hero__title">{appConfig?.app_name ?? "Business Directory"}</h1>
          <p className="hero__subtitle">
            Discover and connect with local businesses.
          </p>
          <BusinessSearchForm
            key={`${searchParams.search}|${searchParams.address}|${searchParams.distance}`}
            defaultValues={searchParams}
            onSearch={handleSearch}
          />
        </div>
      </section>

      <section className="business-listings" id="listings">
        <div className="container">
          <div className="business-listings__header">
            <h2 className="business-listings__heading">
              Local Businesses
              {isFetching && !isLoading && (
                <span className="business-listings__refreshing-indicator">
                  <Loader2 size={16} className="spin" />
                </span>
              )}
            </h2>
            {data && (
              <span className="business-listings__result-count">
                {total} result{total !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {!isLoading && !isError && total > 0 && (
            <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} className="pagination--positioned-top" />
          )}

          {isLoading && (
            <div className="business-status">
              <Loader2 size={32} className="spin" />
              <p>Loading businesses…</p>
            </div>
          )}

          {isError && (
            <div className="business-status business-status--error">
              <p>
                Failed to load businesses. Please check your connection and try
                again.
              </p>
            </div>
          )}

          {!isLoading && !isError && total === 0 && (
            <div className="business-status">
              <p>No businesses found matching your search.</p>
            </div>
          )}

          {!isLoading && !isError && total > 0 && (
            <>
              <div
                className={`business-grid${isFetching ? " business-grid--fading" : ""}`}
              >
                {businesses.map((biz) => (
                  <BusinessCard key={biz.id} business={biz} />
                ))}
              </div>

              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
